import * as carousel from '@zag-js/carousel';
import { normalizeProps, useMachine } from '@zag-js/react';
import dynamic from 'next/dynamic';
import { useEffect, useId, useRef } from 'react';
import { twJoin } from 'tailwind-merge';

import Pagination from 'molecules/InfiniteCarousel/Pagination';

import { TestimonialCardConfigProps } from 'organisms/Cards/Testimonial/config';

import type { CSSProperties, FC } from 'react';

const FeaturedResourceCard = dynamic(() => import('organisms/Cards/FeaturedResourceCard'));
const TestimonialCard = dynamic(() => import('organisms/Cards/Testimonial'));

export interface InfiniteCarouselProps {
  /**
   * The slides to be displayed in the `InfiniteCarousel` component. This can be either an array of `TestimonialsConfigProps['testimonials']` (with `StripMaybe` applied to remove any optional properties), or an array of `BuilderReference<ResourceCard>`.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  slides: any; // StripMaybe<TestimonialsConfigProps['testimonials']> | StripMaybe<HeroConfigProps['carousel']>;
  /**
   * Specifies the type of component to render in the `InfiniteCarousel` component.
   * The valid options are 'testimonialCard' and 'resourceCard'.
   */
  component: 'testimonialCard' | 'resourceCard';
  /**
   * Specifies the spacing between the elements in the infinite carousel, in pixels.
   */
  spacing?: `${number}px`;
}

/**
 * Renders the appropriate component based on the `component` prop passed to the `InfiniteCarousel` component.
 *
 * @param component - The type of component to render, either 'leadershipCard' or 'postCard'.
 * @param props - The props to pass to the rendered component.
 * @returns The rendered component.
 * @throws Error if the `component` prop is not recognized.
 */
const getComponent = (
  component: InfiniteCarouselProps['component'],
  props: TestimonialCardConfigProps & BuilderReference<ResourceCard>,
  current: boolean,
) => {
  switch (component) {
    case 'testimonialCard':
      return <TestimonialCard {...props} current={current} className="h-full" />;
    case 'resourceCard':
      return <FeaturedResourceCard {...props.value.data} />;
    default:
      throw new Error(`Component ${component} is not recognized in InfiniteCarousel`);
  }
};

/**
 * The slides that sit outside the real list as decoration, in render order.
 * Two at each end rather than one: the wrap steps onto the first copy, so the
 * second is what keeps a card peeking on the far side while it does.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const copiesFor = (slides: any[], edge: 'before' | 'after') => {
  const count = slides.length;

  if (count < 2) {
    return [];
  }

  return edge === 'before' ? [slides[(count - 2 + count) % count], slides[count - 1]] : [slides[0], slides[1 % count]];
};

const InfiniteCarousel: FC<InfiniteCarouselProps> = ({ slides, component, spacing }) => {
  const slideCount = slides.length;

  // zag 1.x loops natively: `loop` wraps the page index at both ends, and
  // autoplay implies it. That replaces what this component used to do by hand
  // -- render the slide list three times, start in the middle, and silently
  // jump back with animation disabled whenever an edge copy came into view.
  // The machine owns the cycling now, so the duplication, the `--start-position`
  // offset and the `data-animate` toggle are all gone.
  const service = useMachine(carousel.machine, {
    id: useId(),
    slideCount,
    slidesPerPage: 1,
    slidesPerMove: 1,
    loop: true,
    autoplay: { delay: 3000 },
    spacing,
    // The scroll container spans the viewport (see the item group below), so
    // this gutter is what still lets the first and last slide reach the centre.
    padding: 'calc(50vw - var(--slide-item-size) / 2)',
  });

  const api = carousel.connect(service, normalizeProps);
  const itemGroupProps = api.getItemGroupProps();
  const itemGroupRef = useRef<HTMLDivElement>(null);
  const previousPage = useRef(api.page);

  /**
   * zag's `loop` only wraps the page NUMBER. The scroll container is then told
   * to go to the first slide's snap point, so it animates all the way back
   * across every card -- the carousel visibly runs backwards for a moment.
   * Production never showed that: it rendered the list three times and stepped
   * forward into the next copy, re-centring with the animation off.
   *
   * The decorative copies at either end give us the same trick with one slide
   * instead of n. On a wrap, step onto the copy in the direction of travel,
   * which is identical to the slide being wrapped to, then jump to the real one
   * with the animation off. The visible motion is a single card, forwards.
   */
  useEffect(() => {
    const from = previousPage.current;
    const to = api.page;
    previousPage.current = to;

    const snapPoints = api.pageSnapPoints;
    const last = snapPoints.length - 1;
    const el = itemGroupRef.current;

    if (!el || last < 1) {
      return undefined;
    }

    const wrappedForward = from === last && to === 0;
    const wrappedBackward = from === 0 && to === last;

    if (!wrappedForward && !wrappedBackward) {
      return undefined;
    }

    const step = snapPoints[1] - snapPoints[0];
    const decoy = wrappedForward ? snapPoints[last] + step : snapPoints[0] - step;
    const real = snapPoints[to];

    // `scroll-snap-type: mandatory` refuses to come to rest anywhere that is
    // not a snap point, and the copies deliberately are not snap points, so the
    // browser just ignored the step onto one. Suspend snapping for the length
    // of the step and restore it once we are back on a real slide.
    const snapType = el.style.scrollSnapType;
    const restoreSnap = () => {
      el.style.scrollSnapType = snapType;
    };

    el.style.scrollSnapType = 'none';

    const stepOntoCopy = () => el.scrollTo({ left: decoy, behavior: 'smooth' });

    // The machine issues its own scroll for the new page moments after this
    // effect runs, and whichever call lands last is the one that sticks.
    // Re-issuing ours immediately, on the microtask and on the frame covers
    // every ordering, so the container never visibly sets off backwards first.
    stepOntoCopy();
    queueMicrotask(stepOntoCopy);

    let frame = requestAnimationFrame(() => {
      stepOntoCopy();

      // `scrollend` is still too new to rely on, so watch the position instead
      // and swap the copy for the real slide the moment the step lands.
      const deadline = performance.now() + 1200;

      const swap = () => {
        if (Math.abs(el.scrollLeft - decoy) <= 1 || performance.now() > deadline) {
          el.scrollTo({ left: real, behavior: 'instant' });
          restoreSnap();

          return;
        }

        frame = requestAnimationFrame(swap);
      };

      frame = requestAnimationFrame(swap);
    });

    return () => {
      cancelAnimationFrame(frame);
      restoreSnap();
    };
  }, [api.page]);

  return (
    <div className="flex flex-col gap-12">
      <div className="flex w-full justify-center" {...api.getRootProps()}>
        {/*
          Two widths are in play and they are not the same. This box is the
          carousel's "page": it is what a slide is sized against, and it keeps
          the responsive clamp the design has always used. The scroll container
          inside it is the full viewport, so the neighbouring cards bleed off
          both edges the way they do in production.

          `container-type` is what lets the slide keep measuring itself against
          this box (`cqw`) even though its own parent is viewport-wide.
        */}
        <div
          className={twJoin(
            'w-full [container-type:inline-size]',
            component === 'testimonialCard' ? 'max-w-[620px]' : 'max-w-[1280px]',
          )}
        >
          {/*
            zag 1.x scrolls this element natively, so it forces `overflow-x:
            auto` on it and anything outside its box is clipped -- the old
            implementation translated a `overflow: visible` flex row instead,
            which is why the peek used to come for free. Widening it to the
            viewport brings the peek back; the surrounding `<Section>` is
            `overflow-hidden`, so the extra width is clipped there and never
            reaches the page as a horizontal scrollbar.
          */}
          <div
            {...itemGroupProps}
            ref={itemGroupRef}
            className="mx-[calc(50%-50vw)] w-screen [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={
              {
                ...itemGroupProps.style,
                // zag sizes a slide at the full container width when there is
                // one slide per page. Against the page box that is 50px too
                // wide: a slide has always been the page minus the gap.
                '--slide-item-size': 'calc(100cqw - var(--slide-spacing))',
              } as CSSProperties
            }
          >
            {/*
              A real loop has real ends: at the first slide there is nothing to
              the left and at the last nothing to the right, so the peek went
              lopsided at both edges. The old implementation hid that by
              rendering the whole list three times.

              These are decoration, not slides. zag finds its slides with
              `[data-part=item]` and takes its snap points from the elements
              that carry `scroll-snap-align`, so a plain div carrying neither is
              invisible to the machine while still taking a grid column and
              filling the gap. They are `aria-hidden` because the same quote is
              already in the list.
            */}
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {copiesFor(slides, 'before').map((slide: any, index: number) => (
              <div aria-hidden className="pointer-events-none select-none" key={`before-${slide?.id ?? index}`}>
                {getComponent(component, slide, false)}
              </div>
            ))}
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {slides.map((slide: any, index: number) => (
              <div key={slide.id ?? `slide-${index}`} {...api.getItemProps({ index, snapAlign: 'center' })}>
                {/* `current` enlarges the author name on the active card. 0.76
                    read it off getItemState, which 1.x dropped; `page` is the
                    same number, and it is what the indicators already key on,
                    so exactly one card is highlighted at a time. isInView is
                    NOT the equivalent: the scroll container is viewport-wide,
                    so it reports the neighbours as in view too. */}
                {getComponent(component, slide, api.page === index)}
              </div>
            ))}
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {copiesFor(slides, 'after').map((slide: any, index: number) => (
              <div aria-hidden className="pointer-events-none select-none" key={`after-${slide?.id ?? index}`}>
                {getComponent(component, slide, false)}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Pagination api={api} slides={slides} />
    </div>
  );
};

export default InfiniteCarousel;
